import type { IsoTimestamp } from './common.js';
import type { JobRole } from '../lib/jobRoles.js';

export type UserRole = 'admin' | 'staff';

// User-owned configurable-grid preferences (Stage 5A §1.2 generalises the
// original rollout-only shape to a per-grid map). The built-in 'default'
// preset is computed in the client from each grid's column catalogue -- never
// stored. User presets are uuid'd and capped at 3 PER gridKey.
//
// Stored column-key arrays are opaque strings here; sanitisation (dropping
// unknown keys, appending new factory keys) happens per-grid at render time
// (e.g. src/modules/rollouts/columns.ts#sanitizeOrder), so the wire shape
// stays a simple string[] and server-side validation enforces only the
// envelope (length caps, duplicate-id guards).
export type GridPreset = {
  id: string;              // uuid; never 'default' (reserved for the built-in)
  name: string;
  visibleColumns: string[];
};

export type GridPreferences = {
  activePresetId: string;  // 'default' or the id of a user preset
  presets: GridPreset[];
};

// Per-grid preferences keyed by a namespaced gridKey ('rollout-master',
// 'accounts', and future grids). The map is sparse -- a key only appears once
// the user saves a preset / switches a view for that grid.
export type GridPreferencesByKey = {
  [gridKey: string]: GridPreferences;
};

// A grid's effective preferences come from the user's OWN saved view where they
// have one, otherwise from the org-wide TEAM DEFAULT an admin set for that grid
// (src/lib/gridPreferenceResolution.ts holds the precedence). Surfaced on
// CurrentUser so a dashboard can tell the two apart — "Reset" reverts to the
// team default, which would be a silent no-op to offer someone who has never
// customised.
export type GridPreferenceSource = 'personal' | 'team';

export type GridPreferenceSourcesByKey = {
  [gridKey: string]: GridPreferenceSource;
};

// Known grid keys. The set is open (the preferences endpoint validates only
// the key FORMAT, not membership) so a new grid can ship without a server
// change, but the keys in active use are named here as the single source of
// truth for client call sites.
export const ROLLOUT_MASTER_GRID_KEY = 'rollout-master';
export const ACCOUNTS_GRID_KEY = 'accounts';

// Self-editable AM profile fields (Settings → My Profile). All optional and
// absent on legacy / freshly-bootstrapped user docs -- there is no migration;
// fields materialise on the doc only when the AM first saves their profile.
// Reads coalesce missing fields to null. These drive the franchisee-facing
// email signature (server/modules/previewSends/senderContext.ts): fullName +
// phoneNumber render under "Regards,", and emailSignature, when set, overrides
// the auto-built block entirely.
export type UserProfile = {
  fullName: string | null;
  jobTitle: string | null;
  phoneNumber: string | null;
  emailSignature: string | null;
};

export type User = {
  uid: string;
  email: string;
  displayName: string | null;
  photoURL: string | null;
  role: UserRole;
  // Per-grid column preferences (Stage 5A §1.2). Optional/absent on legacy
  // docs. Pre-Stage-5A docs instead carry a top-level `rolloutGridPreferences`
  // field; the server read translator (server/modules/auth/service.ts
  // #toCurrentUser) folds that into gridPreferences['rollout-master'] on read,
  // so the rename never drops a saved preset regardless of when the
  // migrate-user-grid-preferences-generalize script runs.
  gridPreferences?: GridPreferencesByKey;
  // Promotion Plan spec §3.5: single Team label, used for task assignment
  // (Slice B). Optional/nullable; legacy user docs predate it and read as
  // null. No UI sets it in Slice A — the admin Settings page lands in Slice B.
  team: string | null;
  // Self-editable profile fields. Optional -- see UserProfile.
  fullName?: string | null;
  jobTitle?: string | null;
  phoneNumber?: string | null;
  emailSignature?: string | null;
  // AM morning Slack digest (Advertising Home Phase 2) — OPT-IN, DEFAULT OFF.
  // Absent on every legacy doc and read as false, which is the whole point: no
  // migration, and nobody is DM'd until they ask to be.
  slackDigestEnabled?: boolean;
  // What the person does at Kyck (src/lib/jobRoles.ts). One, from a fixed list;
  // absent/null = no role yet. NOT access -- `role` above is still the only
  // thing that restricts anything.
  jobRole?: JobRole | null;
  // Telemetry, server-written at most once per 15 minutes (not audited).
  lastSignedInAt?: IsoTimestamp | null;
  // Set when an admin turns the user off; cleared on re-enable. A disabled
  // user is refused by the staff auth middleware on every request.
  disabledAt?: IsoTimestamp | null;
  disabledBy?: string | null;
  createdAt: IsoTimestamp;
  updatedAt: IsoTimestamp;
};

// The subset sent over the wire to the client via GET /api/me. The
// gridPreferences field is optional -- users with no stored prefs fall back to
// each grid's built-in 'default' preset. The server always emits the
// generalised gridPreferences shape (legacy rolloutGridPreferences is folded
// in on read), so the client never sees the pre-Stage-5A field. The profile
// fields are included so the Settings → My Profile form can render current
// values.
export type CurrentUser = Pick<
  User,
  | 'uid'
  | 'email'
  | 'displayName'
  | 'photoURL'
  | 'role'
  | 'gridPreferences'
  | 'team'
  | 'fullName'
  | 'jobTitle'
  | 'phoneNumber'
  | 'emailSignature'
  | 'slackDigestEnabled'
  | 'jobRole'
  | 'disabledAt'
  | 'lastSignedInAt'
> & {
  /**
   * `gridPreferences` above is the EFFECTIVE envelope (personal where it
   * exists, else the team default). This says which source won per grid, so the
   * UI can label the view and know whether Reset has anything to revert.
   * Absent on responses that predate the team-default layer.
   */
  gridPreferenceSources?: GridPreferenceSourcesByKey;
};
